import { useCallback, useEffect, useState } from "react";

import { useLanguage } from "../i18n";

interface Todo {
  id: string;
  text_ar: string;
  done_at: string | null;
}

/**
 * One-off intentions — move the basil to morning sun, repot the rosemary.
 * Nothing schedules these and nothing notifies about them; they are here so
 * they do not live only in a spreadsheet.
 */
export function Todos({ specimenId }: { specimenId: string }) {
  const { t } = useLanguage();
  const [todos, setTodos] = useState<Todo[]>([]);
  const [draft, setDraft] = useState("");

  const load = useCallback(async () => {
    const response = await fetch(`/v1/specimens/${specimenId}/todos`);
    if (!response.ok) return;
    const body: { todos: Todo[] } = await response.json();
    setTodos(body.todos);
  }, [specimenId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function add(event: React.FormEvent) {
    event.preventDefault();
    if (!draft.trim()) return;
    await fetch(`/v1/specimens/${specimenId}/todos`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text_ar: draft.trim() }),
    });
    setDraft("");
    void load();
  }

  async function toggle(id: string) {
    await fetch(`/v1/specimens/${specimenId}/todos/${id}/toggle`, { method: "POST" });
    void load();
  }

  async function remove(id: string) {
    await fetch(`/v1/specimens/${specimenId}/todos/${id}`, { method: "DELETE" });
    void load();
  }

  return (
    <section className="todos">
      <h2>{t.thingsToDo}</h2>

      {todos.length === 0 && <p className="muted">{t.todoEmpty}</p>}

      <ul className="todo-list">
        {todos.map((todo) => (
          <li key={todo.id} className={todo.done_at ? "done" : undefined}>
            <label>
              <input type="checkbox" checked={Boolean(todo.done_at)} onChange={() => void toggle(todo.id)} />
              <span lang="ar">{todo.text_ar}</span>
            </label>
            <button type="button" className="remove" onClick={() => void remove(todo.id)} aria-label="Remove">
              ×
            </button>
          </li>
        ))}
      </ul>

      <form className="todo-add" onSubmit={add}>
        <input
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t.todoPlaceholder}
          lang="ar"
        />
        <button type="submit" className="action" disabled={!draft.trim()}>
          {t.add}
        </button>
      </form>
    </section>
  );
}
